/**
 * Legal-text constants. Versioned so a counsel-driven update bumps
 * the version, every user with a stale acceptance gets re-prompted
 * on next sensitive action, and the JoinAgreement audit trail
 * resolves to the exact text the user agreed to.
 *
 * Version format: 'YYYY.MM.DD-vN'. Bump N for same-day revisions;
 * roll the date for first-revision-of-the-day. Never reuse a
 * version string with different text.
 */
export const CURRENT_JOIN_AGREEMENT_VERSION = '2026.05.01-v2';
// ── Platform terms versioning (TermsVersion / TermsAcceptance) ───────────────
//
// The building-join agreement above is one narrow, per-building acceptance.
// These are the PLATFORM-WIDE versioned legal documents each user may be
// required to (re-)accept: the driver Terms of Service, the payout/earning
// terms (gating any earning surface), and the Sales Partner terms. Each has a
// current version string + a placeholder body seeded into the TermsVersion
// collection; bumping a version (publishing a new TermsVersion row) forces
// re-acceptance on the next sign-in. Counsel replaces the placeholder bodies
// before prod — the versioning + acceptance machinery is what ships now.
//
// IMPORTANT: none of this text asserts marketing consent, and accepting terms
// never changes it. Marketing email has its own record, User.marketing_consent
// (lib/marketingConsent.ts): since 23 October 2026 drivers are enrolled by
// default and can opt out, Canadian drivers only while they have a paid
// session in the last 2 years. The signup box (User.marketing_opt_in) is an
// express opt-in on top, never bundled into terms acceptance.
export const TERMS_DOC_TYPES = [
    'driver_tos', 'payout_terms', 'sp_terms',
    'host_terms', 'carbon_terms', 'fleet_terms', 'ai_terms',
    'site_host_terms',
];
/** Human labels for the three doc types (admin + accept-screen headings). */
export const TERMS_DOC_LABELS = {
    driver_tos: 'Terms of Service',
    payout_terms: 'Payout Terms',
    sp_terms: 'Sales Partner Program Terms',
    host_terms: 'Host Program Terms',
    carbon_terms: 'Carbon Credit Program Terms',
    fleet_terms: 'Fleet Terms',
    ai_terms: 'AI Assistant Terms',
    site_host_terms: 'Site Host Terms',
};
/**
 * The BOOTSTRAP version string per doc type, paired with the placeholder
 * bodies below. Only ever served when a doc type has NO TermsVersion rows at
 * all, i.e. a fresh environment before the seed runs (see resolveCurrentTerms,
 * which now refuses to fall back when history exists).
 *
 * EVERY ENTRY IS 2026.07.12-v1 ON PURPOSE, and none of them may ever be set to
 * the version of a real document in legal/. These strings label PLACEHOLDER
 * TEXT. The real documents all publish as 2026.07.29-v1. If a bootstrap entry
 * carried that same string, a user who accepted placeholder text during
 * bootstrap would be recorded as having accepted version 2026.07.29-v1, the
 * real document would publish under the identical version, and the gate would
 * see a matching acceptance and never re-prompt them. They would be bound to a
 * document they were never shown.
 *
 * So: bumping a real document means editing the file in legal/, never this.
 */
export const CURRENT_TERMS_VERSIONS = {
    driver_tos: '2026.07.12-v1',
    payout_terms: '2026.07.12-v1',
    sp_terms: '2026.07.12-v1',
    host_terms: '2026.07.12-v1',
    carbon_terms: '2026.07.12-v1',
    fleet_terms: '2026.07.12-v1',
    ai_terms: '2026.07.12-v1',
    site_host_terms: '2026.07.12-v1',
};
const PLACEHOLDER_TAIL = '\n\nThis is placeholder text and will be replaced with counsel-approved '
    + 'language in a future release; you may be asked to re-accept at that time. '
    + 'Accepting confirms you have read and understand the placeholder above.';
/** Placeholder bodies, seeded into TermsVersion. Counsel replaces before prod. */
export const TERMS_PLACEHOLDER_BODIES = {
    driver_tos: '1. Using the EV Initiative app\n'
        + 'EV Initiative ("EVI") provides the software that helps you find chargers, start '
        + 'sessions, manage your account, and earn rewards. You agree to use it lawfully and '
        + 'to keep your account credentials secure.\n\n'
        + '2. Charging and payments\n'
        + 'Charging sessions are provided by station operators; payment for a session flows '
        + 'from your saved card through Stripe Connect to the operator. EVI is a '
        + 'payment-facilitation platform, not the merchant of record for a session.\n\n'
        + '3. Rewards and points\n'
        + 'Points and rewards have no cash value, are non-transferable except where the app '
        + 'expressly allows, and may be adjusted for abuse or error.\n\n'
        + '4. Limitation of liability\n'
        + 'The platform is provided "as is." EVI is not liable for charger malfunction, '
        + 'electrical issues at an operator site, or vehicle-compatibility problems; aggregate '
        + 'platform liability is capped at the platform fees you paid in the prior twelve months.'
        + PLACEHOLDER_TAIL,
    payout_terms: '1. Earning on EV Initiative\n'
        + 'These terms apply once you enable an earning surface — monetizing a home charger, '
        + 'completing Sales Partner enrollment, or onboarding as a host. They govern how and '
        + 'when EVI pays you.\n\n'
        + '2. Payouts via Stripe Connect\n'
        + 'Earnings are paid to your connected Stripe account. You are responsible for '
        + 'completing Stripe Connect onboarding and keeping your payout details current. EVI '
        + 'does not hold your funds beyond normal settlement timing.\n\n'
        + '3. Commission and fees\n'
        + 'EVI retains a platform commission on monetized sessions, disclosed in-app before you '
        + 'enable earning. Applicable processor fees and taxes may be withheld or reported as '
        + 'required by law.\n\n'
        + '4. Taxes\n'
        + 'You are responsible for any taxes on your earnings. EVI may issue tax forms and '
        + 'collect tax information where required.\n\n'
        + '5. Clawbacks\n'
        + 'Refunded, disputed, or fraudulent sessions may be reversed against your balance.'
        + PLACEHOLDER_TAIL,
    sp_terms: '1. The Sales Partner program\n'
        + 'As a Sales Partner ("SP") you refer and help deploy chargers on the EV Initiative '
        + 'network. Commission on qualifying sessions activates once you meet the program '
        + 'qualification threshold, and is subject to the Payout & Earnings Terms.\n\n'
        + '2. Conduct\n'
        + 'You represent EV Initiative honestly and comply with all applicable laws and the '
        + 'program requirements communicated to you. Misrepresentation or abuse may end your '
        + 'participation and forfeit unpaid commission.\n\n'
        + '3. Independent relationship\n'
        + 'You are an independent participant, not an employee or agent of EVI, and may not '
        + 'bind EVI to any obligation.\n\n'
        + '4. Program changes\n'
        + 'Commission rates, tiers, and qualification rules may change with notice; continued '
        + 'participation after a change constitutes acceptance of the updated terms.'
        + PLACEHOLDER_TAIL,
    // The four types added with the counsel-final document set. Their real
    // bodies live in legal/*.md and are published by seed-terms-versions.ts;
    // these exist only so a fresh, unseeded environment has something to serve.
    // Kept deliberately SHORT: a long placeholder invites someone to mistake it
    // for the real thing.
    host_terms: 'You make a charger you own available to other drivers and receive a share of what '
        + 'they pay. You are responsible for your equipment, your premises, and for confirming '
        + 'you are permitted to host paid charging at your location.'
        + PLACEHOLDER_TAIL,
    carbon_terms: 'Energy delivered at an enrolled charger generates carbon credit value, which only a '
        + 'network operator can register and claim. You assign us the environmental attributes '
        + 'for that purpose and receive a share of what is actually realised.'
        + PLACEHOLDER_TAIL,
    fleet_terms: 'A fleet account pays for charging done by drivers it authorises. The account holder '
        + 'controls who may charge and can see the charging it pays for. A driver\'s personal '
        + 'payment method is never the payer of last resort for a fleet-sponsored session.'
        + PLACEHOLDER_TAIL,
    ai_terms: 'The Platform includes an AI assistant. It is software and can be wrong, its output '
        + 'does not bind us, and an action it takes at your instruction is treated as yours. '
        + 'Do not enter payment credentials, government identification, or health information.'
        + PLACEHOLDER_TAIL,
    // NEVER SERVED. site_host_terms is published-only (termsGate.ts
    // resolvePublishedTerms): with no TermsVersion row it is simply not required,
    // so nobody is ever asked to accept this placeholder. It exists because the
    // Record type demands an entry.
    site_host_terms: 'If your site agreement provides for it, we reimburse your property for the electricity '
        + 'its chargers use, once a bank account is connected for the location.'
        + PLACEHOLDER_TAIL,
};
/**
 * The Site Host Terms (legal/SITE_HOST_TERMS.md). Accepted by Building Admins
 * and Property Managers, once per location they manage, and only once a real
 * version is published: before the flip there is no TermsVersion row and the
 * document is not required anywhere.
 */
export const SITE_HOST_TERMS_DOC_TYPE = 'site_host_terms';
/** The role templates that accept the Site Host Terms ("Building Admin" in
 *  SITE_HOST_TERMS.md section 2 means either of these roles). charger_owner is
 *  deliberately absent: an owner of the chargers is not the property. */
export const SITE_HOST_TERMS_TEMPLATES = ['building_admin', 'property_manager'];
/**
 * The first Launch Date the Site Host Terms reach (SITE_HOST_TERMS.md section
 * 1: "for a Site whose Launch Date is on or after 2 October 2026"). Midnight
 * Pacific, where EV Initiative is. A Site launched earlier stays under its
 * existing agreement until a Transition Notice (section 13), so the bank
 * banner keeps telling it so after its 60 days.
 */
export const SITE_HOST_TERMS_FIRST_LAUNCH_AT = '2026-10-02T07:00:00.000Z';
/** Doc types that gate EARNING surfaces (payout terms must be accepted before
 *  enabling home monetization, SP enrollment, or host Connect onboarding). */
export const PAYOUT_TERMS_DOC_TYPE = 'payout_terms';
/**
 * Placeholder legal text. Renders as-is in the join modal's
 * scrollable acceptance card. Counsel review still pending —
 * the bracketed warning banner that previously prefixed the
 * body was removed in v2 because it was visible to drivers in
 * production join flows; the body itself remains the same
 * placeholder until counsel signs off.
 *
 * Version-bump policy: any change to the body OR header MUST
 * bump CURRENT_JOIN_AGREEMENT_VERSION above so users with a
 * stale acceptance get re-prompted (the v1 → v2 bump that
 * landed alongside this banner removal is the canonical
 * example).
 */
export const JOIN_AGREEMENT_PLACEHOLDER_TEXT = `1. Platform vs operator role
EV Initiative ("EVI") provides the software platform that connects you with charging-station operators ("operators"). The operator running the building you join is responsible for the chargers, pricing, access rules, on-site support, and billing relationship with you. EVI does not own the charging hardware at this site, set its prices, or guarantee its availability.

2. Dispute routing
Disputes about a charging session, billing, or station access route to the operator first. Contact the operator's admin via the contact details surfaced in your Profile or session-receipt screens. EVI provides escalation support when the operator is unresponsive, but the operator is the primary counterparty.

3. Billing relationship via Stripe Connect
Charging-session payments flow from your saved card through Stripe Connect to the operator's account. EVI is a payment-facilitation platform here, not the merchant of record for the session. Stripe's Terms of Service govern the payment leg.

4. EVI commission disclosure
EVI receives a commission on each session, calculated as a percentage of the operator's gross revenue. The default commission is 10%; specific rates may differ per operator and are disclosed in your operator's terms. The amount you pay does not change based on commission.

5. Limitation of liability
EVI provides the platform "as is" and is not liable for damages arising from charger malfunction, electrical issues at the operator's site, vehicle compatibility problems, or operator-driven access disputes. The operator's terms govern claims related to physical service. EVI's aggregate liability for platform issues is capped at the fees you paid through the platform in the prior twelve months.

6. Data sharing with building admin
By joining a building you authorize EVI to share with that building's admin: your name, email, phone (if provided), session history at that building, payment success/failure status, and your assigned access scope. The admin uses this data to manage station access and resolve disputes. Data is not shared with other tenants in the building or with EVI partners.

By accepting these terms you confirm you have read and understand the placeholder text above. This placeholder will be replaced with counsel-approved text in a future release; you may be asked to re-accept at that time.`;
//# sourceMappingURL=legal.js.map